import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  FormLabel,
  Collapse,
  Chip,
  ClickAwayListener,
  useTheme
} from '@mui/material';
import config from '../config';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Email,
  ScheduleSend,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Visibility as ViewIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import ReminderEmailDialog from './ReminderEmailDialog';
import ManualBookingBoxes from './ManualBookingBoxes';
import { functionOn, AssistantFunctions } from './AssistantSettingsDialog';
import ExpiryRing from './ExpiryRing';
import MillosLogo from './MillosLogo';
import { getCategoryColor } from '../theme';
import { useCopilot } from '../contexts/CopilotContext';
import { useAuth } from '../contexts/AuthContext';

// The provider is emailed first and the tenant only after the provider replies, so "sent" means the provider's email once one exists.
const outreachSent = (emails: Array<{ audience: string; status: string }>) => {
  const stage1 = emails.filter(e => e.audience === 'provider' || e.audience === 'tenant');
  const lead = stage1.some(e => e.audience === 'provider') ? stage1.filter(e => e.audience === 'provider') : stage1;
  return lead.length > 0 && lead.every(e => e.status === 'sent');
};

interface ActiveReminder {
  id: string;
  certificate_id: string;
  certificate_title: string;
  certificate_type: string;
  certificate_type_category?: string;
  property_address: string;
  normalized_address?: string;
  flat_number?: string;
  expiry_date: string;
  issue_date?: string | null;
  reminder_days_before: number;
  reminder_date: string;
  status: string;
  email_notification_enabled: boolean;
  email_sent_at: string | null;
  email_sent_to: string | null;
  recipient_name: string | null;
  provider_id: string | null;
  custom_contact_name: string | null;
  custom_contact_email: string | null;
  copilot_paused?: boolean;
  google_event_id?: string | null;
  updated_at?: string;
  recipient_mode?: 'both' | 'provider' | 'tenant' | 'none';
}

interface ActiveRemindersProps {
  refreshTrigger?: number;
  onReminderChange?: () => void;
  assistantEnabled?: boolean;
  assistantFunctions?: AssistantFunctions;
}

const ActiveReminders: React.FC<ActiveRemindersProps> = ({ refreshTrigger = 0, onReminderChange, assistantEnabled = true, assistantFunctions = {} }) => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  const { showMessage } = useCopilot();
  const { user } = useAuth();
  const [reminders, setReminders] = useState<ActiveReminder[]>([]);
  const [loading, setLoading] = useState(false);
  const [reminderEmails, setReminderEmails] = useState<{[key: string]: any[]}>({});
  const [availabilityResponses, setAvailabilityResponses] = useState<{[key: string]: any[]}>({});
  const [availabilityCoordination, setAvailabilityCoordination] = useState<{[key: string]: any}>({});
  const [renewalStatus, setRenewalStatus] = useState<{[key: string]: any}>({});
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [tenantStatus, setTenantStatus] = useState<{[key: string]: any}>({});
  const [addTenantOpenFor, setAddTenantOpenFor] = useState<string | null>(null);
  const [addTenantName, setAddTenantName] = useState('');
  const [addTenantEmail, setAddTenantEmail] = useState('');
  const [savingTenant, setSavingTenant] = useState(false);
  const [providerStatus, setProviderStatus] = useState<{[key: string]: any}>({});
  const [addProviderOpenFor, setAddProviderOpenFor] = useState<string | null>(null);
  const [addProviderName, setAddProviderName] = useState('');
  const [addProviderEmail, setAddProviderEmail] = useState('');
  const [selectedExistingProviderId, setSelectedExistingProviderId] = useState('');
  const [savingProvider, setSavingProvider] = useState(false);
  const [addProviderConflict, setAddProviderConflict] = useState<{ message: string; existingProvider: any } | null>(null);
  const [confirmingSlot, setConfirmingSlot] = useState<{[key: string]: boolean}>({});
  const [coordinationDetailsOpenFor, setCoordinationDetailsOpenFor] = useState<string | null>(null);
  const [copilotOffInfoOpenFor, setCopilotOffInfoOpenFor] = useState<string | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<ActiveReminder | null>(null);
  const [editingDate, setEditingDate] = useState('');
  const [editingTime, setEditingTime] = useState('09:30');
  const [editingDaysBefore, setEditingDaysBefore] = useState(21);
  const [editingReminderOption, setEditingReminderOption] = useState<string>('21');
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addingToCalendar, setAddingToCalendar] = useState<Record<string, boolean>>({});
  const [sentInfoOpenId, setSentInfoOpenId] = useState<string | null>(null);
  const [deletingReminder, setDeletingReminder] = useState<ActiveReminder | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailReminder, setEmailReminder] = useState<ActiveReminder | null>(null);
  const [editEmailDialogOpen, setEditEmailDialogOpen] = useState(false);
  const [editingEmailReminder, setEditingEmailReminder] = useState<ActiveReminder | null>(null);
  const [providers, setProviders] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [customContactName, setCustomContactName] = useState('');
  const [customContactEmail, setCustomContactEmail] = useState('');
  const [isTogglingEmail, setIsTogglingEmail] = useState(false);
  const [sendingNow, setSendingNow] = useState<{[key: string]: boolean}>({});
  const [chasingEmail, setChasingEmail] = useState<{[key: string]: boolean}>({});

  const [emailDraftDialogOpen, setEmailDraftDialogOpen] = useState(false);
  const [selectedReminderId, setSelectedReminderId] = useState<string | null>(null);
  const [selectedReminderForEmail, setSelectedReminderForEmail] = useState<ActiveReminder | null>(null);
  const [viewingEmailId, setViewingEmailId] = useState<string | null>(null);
  const [expandedCopilotIds, setExpandedCopilotIds] = useState<Set<string>>(() => {
    try {
      return new Set<string>(JSON.parse(localStorage.getItem('expandedCopilotIds') || '[]'));
    } catch {
      return new Set<string>();
    }
  });

  useEffect(() => {
    localStorage.setItem('expandedCopilotIds', JSON.stringify(Array.from(expandedCopilotIds)));
  }, [expandedCopilotIds]);

  const toggleCopilotStatus = (reminderId: string) => {
    setExpandedCopilotIds(prev => {
      const next = new Set(prev);
      if (next.has(reminderId)) next.delete(reminderId);
      else next.add(reminderId);
      return next;
    });
  };

  const handleViewEmail = (reminder: ActiveReminder, emailId: string) => {
    setSelectedReminderId(reminder.id);
    setSelectedReminderForEmail(reminder);
    setViewingEmailId(emailId);
    setEmailDraftDialogOpen(true);
  };

  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
  // Which month tab is active per expanded year — mirrors selectedUnit in PropertyCard
  // (year ~ property, month ~ flat). Falls back to the earliest month if unset/stale.
  const [selectedMonths, setSelectedMonths] = useState<Record<string, string>>({});

  const loadProviders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const response = await fetch(`${config.apiUrl}/api/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      console.error('Error loading providers:', err);
    }
  };

  const loadActiveReminders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }

      let response = await fetch(`${config.apiUrl}/api/reminders/active-with-certificates?include_completed=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        response = await fetch(`${config.apiUrl}/api/reminders?status=active`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      if (response.ok) {
        const data = await response.json();
        setReminders(data.reminders || []);
        await loadReminderEmails(data.reminders || []);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to load reminders');
      }
    } catch (err) {
      showMessage({ text: 'Failed to load reminders. Please refresh the page.' });
      console.error('Error loading reminders:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadReminderEmails = async (reminderList: ActiveReminder[]) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const emailsMap: {[key: string]: any[]} = {};
    const responsesMap: {[key: string]: any[]} = {};
    const coordinationMap: {[key: string]: any} = {};
    const tenantStatusMap: {[key: string]: any} = {};
    const providerStatusMap: {[key: string]: any} = {};
    const renewalMap: {[key: string]: any} = {};
    await Promise.all(
      reminderList.map(async (reminder) => {
        try {
          const response = await fetch(`${config.apiUrl}/api/reminder-emails/${reminder.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.emails.length > 0) {
              emailsMap[reminder.id] = data.emails;
            }
          }

          // Also fetch availability responses
          const availResponse = await fetch(`${config.apiUrl}/api/reminders/${reminder.id}/availability-responses`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (availResponse.ok) {
            const availData = await availResponse.json();
            if (availData.success && availData.responses.length > 0) {
              responsesMap[reminder.id] = availData.responses;
            }
          }

          // Restore any previously computed coordination (404 = none yet, not an error)
          const coordResponse = await fetch(`${config.apiUrl}/api/reminders/${reminder.id}/coordination`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (coordResponse.ok) {
            const coordData = await coordResponse.json();
            if (coordData.success && coordData.coordination) {
              coordinationMap[reminder.id] = coordData.coordination;
            }
          }

          // Last step of the flow: has the renewed certificate arrived / been approved
          const renewalResponse = await fetch(`${config.apiUrl}/api/reminders/${reminder.id}/renewal-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (renewalResponse.ok) {
            const renewalData = await renewalResponse.json();
            if (renewalData.success) renewalMap[reminder.id] = renewalData;
          }

          // Tenant-on-file status — drives the "Tenant not found" card state
          const tenantStatusResponse = await fetch(`${config.apiUrl}/api/reminders/${reminder.id}/tenant-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (tenantStatusResponse.ok) {
            const tenantStatusData = await tenantStatusResponse.json();
            if (tenantStatusData.success) {
              tenantStatusMap[reminder.id] = tenantStatusData;
            }
          }

          // Provider-on-file status — drives the "Provider not found" card state
          const providerStatusResponse = await fetch(`${config.apiUrl}/api/reminders/${reminder.id}/provider-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (providerStatusResponse.ok) {
            const providerStatusData = await providerStatusResponse.json();
            if (providerStatusData.success) {
              providerStatusMap[reminder.id] = providerStatusData;
            }
          }
        } catch (err) {
          console.error(`Error loading emails for reminder ${reminder.id}:`, err);
        }
      })
    );
    // Merge rather than replace — this can be called with just a subset of reminders (polling
    // while one Copilot panel is expanded), and a full replace would wipe every other reminder's
    // already-loaded data out of state.
    setReminderEmails(prev => ({ ...prev, ...emailsMap }));
    setAvailabilityResponses(prev => ({ ...prev, ...responsesMap }));
    setAvailabilityCoordination(prev => ({ ...prev, ...coordinationMap }));
    setTenantStatus(prev => ({ ...prev, ...tenantStatusMap }));
    setProviderStatus(prev => ({ ...prev, ...providerStatusMap }));
    setRenewalStatus(prev => ({ ...prev, ...renewalMap }));
  };

  const handleAddTenant = async (reminderId: string) => {
    if (!addTenantName.trim() || !addTenantEmail.trim()) {
      showMessage({ text: 'Name and email are required.' });
      return;
    }
    setSavingTenant(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/add-tenant`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: addTenantName.trim(), email: addTenantEmail.trim() })
      });
      const data = await response.json();
      if (data.success) {
        showMessage({ text: 'Tenant added.' });
        setAddTenantOpenFor(null);
        setAddTenantName('');
        setAddTenantEmail('');
        await loadReminderEmails(reminders.filter(r => r.id === reminderId));
      } else {
        showMessage({ text: data.error || 'Failed to add tenant.' });
      }
    } catch (err) {
      console.error('Error adding tenant:', err);
      showMessage({ text: 'Failed to add tenant. Please try again.' });
    } finally {
      setSavingTenant(false);
    }
  };

  const handleSetCopilotPaused = async (reminderId: string, paused: boolean) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/copilot-pause`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused })
      });
      const data = await response.json();
      if (data.success) {
        setReminders(prev => prev.map(r => r.id === reminderId ? { ...r, copilot_paused: paused } : r));
        if (!paused) await loadReminderEmails(reminders.filter(r => r.id === reminderId));
      } else {
        showMessage({ text: data.error || 'Failed to update.' });
      }
    } catch (err) {
      console.error('Error setting copilot paused state:', err);
      showMessage({ text: 'Failed to update. Please try again.' });
    }
  };

  const handleAddProvider = async (reminderId: string) => {
    // Either link an already-registered provider (dropdown) or register a brand new one — the
    // dropdown takes priority since picking one and still having name/email filled in from a
    // previous attempt shouldn't accidentally register a duplicate.
    const linkingExisting = !!selectedExistingProviderId;
    if (!linkingExisting && (!addProviderName.trim() || !addProviderEmail.trim())) {
      showMessage({ text: 'Name and email are required, or select an existing provider.' });
      return;
    }
    setSavingProvider(true);
    setAddProviderConflict(null);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/add-provider`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(
          linkingExisting
            ? { provider_id: selectedExistingProviderId }
            : { name: addProviderName.trim(), email: addProviderEmail.trim() }
        )
      });
      const data = await response.json();
      if (data.success) {
        showMessage({ text: 'Provider added.' });
        setAddProviderOpenFor(null);
        setAddProviderName('');
        setAddProviderEmail('');
        setSelectedExistingProviderId('');
        await loadReminderEmails(reminders.filter(r => r.id === reminderId));
      } else if (response.status === 409 && data.existing_provider) {
        // Same email already registered to a different provider record — offer to link that
        // one instead of silently failing or creating a duplicate.
        setAddProviderConflict({ message: data.error, existingProvider: data.existing_provider });
      } else {
        showMessage({ text: data.error || 'Failed to add provider.' });
      }
    } catch (err) {
      console.error('Error adding provider:', err);
      showMessage({ text: 'Failed to add provider. Please try again.' });
    } finally {
      setSavingProvider(false);
    }
  };

  const handleSetTenantNotifyMode = async (reminderId: string, mode: 'self' | null) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/tenant-notify-mode`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      });
      const data = await response.json();
      if (data.success) {
        setTenantStatus(prev => ({ ...prev, [reminderId]: { ...prev[reminderId], tenant_notify_mode: mode } }));
      } else {
        showMessage({ text: data.error || 'Failed to update.' });
      }
    } catch (err) {
      console.error('Error updating tenant notify mode:', err);
      showMessage({ text: 'Failed to update. Please try again.' });
    }
  };

  const handleConfirmVisitDate = async (reminderId: string, slot: { date: string; start_time: string; end_time: string }) => {
    setConfirmingSlot(prev => ({ ...prev, [reminderId]: true }));
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/confirm-visit-date`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(slot)
      });
      const data = await response.json();
      if (data.success) {
        setAvailabilityCoordination(prev => ({ ...prev, [reminderId]: data.coordination }));
        await loadReminderEmails(reminders.filter(r => r.id === reminderId));
      } else {
        showMessage({ text: data.error || 'Failed to confirm visit date.' });
      }
    } catch (err) {
      console.error('Error confirming visit date:', err);
      showMessage({ text: 'Failed to confirm visit date. Please try again.' });
    } finally {
      setConfirmingSlot(prev => ({ ...prev, [reminderId]: false }));
    }
  };

  const handleCloseEmailDraft = () => {
    setEmailDraftDialogOpen(false);
    setSelectedReminderId(null);
    setSelectedReminderForEmail(null);
    setViewingEmailId(null);
    loadActiveReminders();
  };

  // "Send now" — fires the Stage 1 emails (provider + tenant) immediately, bypassing the reminder date
  const handleSendNow = async (reminderId: string) => {
    setSendingNow(prev => ({ ...prev, [reminderId]: true }));
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/send-stage1-emails`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        showMessage({ text: `Emails sent (${data.sent_count || 2} sent)` });
        await loadActiveReminders();
      } else {
        showMessage({ text: data.error || 'Failed to send emails.' });
      }
    } catch (err) {
      console.error('Error sending emails:', err);
      showMessage({ text: 'Failed to send emails. Please try again.' });
    } finally {
      setSendingNow(prev => ({ ...prev, [reminderId]: false }));
    }
  };


  // Chase a provider/tenant who has not replied, before the automatic chase falls due.
  const handleChaseNow = async (reminder: ActiveReminder, emailId: string) => {
    setChasingEmail(prev => ({ ...prev, [emailId]: true }));
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminder-emails/${emailId}/chase`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        showMessage({ text: 'I sent a follow-up email.' });
        await loadReminderEmails([reminder]);
      } else {
        showMessage({ text: data.error || 'Failed to send the follow-up.' });
      }
    } catch {
      showMessage({ text: 'Failed to send the follow-up. Please try again.' });
    } finally {
      setChasingEmail(prev => ({ ...prev, [emailId]: false }));
    }
  };

  useEffect(() => {
    loadActiveReminders();
  }, [refreshTrigger]);

  // While a Copilot Status panel is open and waiting on the AI (Stage 1 sent but no coordination
  // yet), poll for tenant/provider responses + coordination every few seconds so the flow
  // advances live instead of only on the next manual refresh.
  useEffect(() => {
    const pendingIds = Array.from(expandedCopilotIds).filter(id => {
      // Covers both a confirmed date and a stored "no overlap found" result — either way,
      // there's nothing left to poll for and no reason to keep calling the AI.
      // A finished flow needs no polling. After a booked visit we keep polling for the renewed certificate.
      if (['completed', 'archived'].includes(renewalStatus[id]?.reminder_status ?? reminders.find(r => r.id === id)?.status ?? '')) return false;
      if (availabilityCoordination[id]?.recommended_date) return true;
      if (availabilityCoordination[id]) return false;
      return outreachSent(reminderEmails[id] || []);
    });
    if (pendingIds.length === 0) return;

    const pendingReminders = reminders.filter(r => pendingIds.includes(r.id));
    const interval = setInterval(() => {
      loadReminderEmails(pendingReminders);
    }, 8000);
    return () => clearInterval(interval);
  }, [expandedCopilotIds, reminders, reminderEmails, availabilityCoordination, renewalStatus]);

  // Expand only the first (earliest) year on load
  useEffect(() => {
    if (reminders.length > 0) {
      const firstYear = reminders
        .map(r => new Date(r.expiry_date).getFullYear().toString())
        .sort()[0];
      setExpandedYears(new Set([firstYear]));
    }
  }, [reminders.length]);

  const formatAvailability = (slots: Array<{ date: string; start_time: string; end_time: string }> | undefined, notes: string | undefined | null): string => {
    const slotsPart = slots && slots.length > 0
      ? slots.map(s => `${new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} (${s.start_time}-${s.end_time})`).join(', ')
      : 'no specific slots';
    return notes ? `${slotsPart} — "${notes}"` : slotsPart;
  };

  const formatSentAt = (iso: string | undefined | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const sentInfoIcon = (email: { id: string; status?: string; updated_at?: string; recipient_email?: string | null; delivery_error?: string | null; chase?: { chase_count: number; last_chased_at: string | null } }, extra?: { label: string; lines: string[]; repliedAt?: string | null }) => {
    const label = email.status === 'sent' ? formatSentAt(email.updated_at) : null;
    if (!label) return null;
    const failed = !!email.delivery_error;
    const toggle = () => setSentInfoOpenId(prev => (prev === email.id ? null : email.id));
    const details = (
      <Box sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}>
        <div>Sent on {label}</div>
        {email.recipient_email && <div>To: {email.recipient_email}</div>}
        {failed && <div>Status: Delivery failed. {email.delivery_error}</div>}
        {email.chase && email.chase.chase_count > 0 && (
          <div>Chased {email.chase.chase_count} time{email.chase.chase_count === 1 ? '' : 's'}, last on {formatSentAt(email.chase.last_chased_at)}</div>
        )}
        {extra?.repliedAt && <div>Replied on {formatSentAt(extra.repliedAt)}</div>}
        {extra && extra.lines.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <div>{extra.label}</div>
            {extra.lines.map(l => <div key={l}>{l}</div>)}
          </Box>
        )}
      </Box>
    );
    return (
      <ClickAwayListener onClickAway={() => setSentInfoOpenId(prev => (prev === email.id ? null : prev))}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Tooltip open={sentInfoOpenId === email.id} title={details} placement="top" arrow disableFocusListener disableHoverListener disableTouchListener>
            <IconButton size="small" onClick={e => { e.stopPropagation(); toggle(); }} sx={{ p: 0.25, color: failed ? palette.tokens.error : palette.tokens.muted, '&:hover': { color: failed ? palette.tokens.error : palette.tokens.ink } }}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </ClickAwayListener>
    );
  };

  const getFlatDisplay = (flatNum: string | undefined): string => {
    if (!flatNum ||
        flatNum === 'flat_number_not_found' ||
        flatNum === '{flat_number_not_found}' ||
        flatNum.includes('flat_number_not_found')) {
      return '';
    }
    let flatValue: any = flatNum;
    if (typeof flatValue === 'string') {
      if ((flatValue.startsWith('[') && flatValue.endsWith(']')) ||
          (flatValue.startsWith('{') && flatValue.endsWith('}'))) {
        try {
          const parsed = JSON.parse(flatValue);
          if (Array.isArray(parsed)) {
            if (parsed.length === 0) return '';
            flatValue = parsed[0];
          } else if (typeof parsed === 'object' && parsed !== null) {
            const values = Object.values(parsed);
            if (values.length === 0) return '';
            flatValue = values[0] ? String(values[0]) : '';
          }
        } catch {
          flatValue = flatValue.replace(/[\[\]{}\"]/g, '').trim();
        }
      }
    }
    if (Array.isArray(flatValue)) {
      if (flatValue.length === 0) return '';
      flatValue = flatValue[0];
    }
    flatValue = flatValue.toString().trim();
    if (!flatValue) return '';
    const displayValue = flatValue.includes('Flat') ? flatValue : `Flat ${flatValue}`;
    return ` · ${displayValue}`;
  };

  const toggleYear = (year: string) => {
    setExpandedYears(prev => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const handleEditReminder = (reminder: ActiveReminder) => {
    setEditingReminder(reminder);
    const reminderDateTime = new Date(reminder.reminder_date);
    setEditingDate(reminderDateTime.toISOString().split('T')[0]);
    const hours = reminderDateTime.getHours().toString().padStart(2, '0');
    const minutes = reminderDateTime.getMinutes().toString().padStart(2, '0');
    setEditingTime(`${hours}:${minutes}`);
    const days = reminder.reminder_days_before;
    setEditingDaysBefore(days);
    if (days === 14) setEditingReminderOption('14');
    else if (days === 21) setEditingReminderOption('21');
    else setEditingReminderOption('custom');
    setEditDialogOpen(true);
  };

  const handleEditEmailRecipient = (reminder: ActiveReminder) => {
    setEditingEmailReminder(reminder);
    if (reminder.provider_id) {
      setSelectedProvider(reminder.provider_id);
      setCustomContactName('');
      setCustomContactEmail('');
    } else if (reminder.custom_contact_name) {
      setSelectedProvider('');
      setCustomContactName(reminder.custom_contact_name);
      setCustomContactEmail(reminder.custom_contact_email || '');
    } else {
      setSelectedProvider('');
      setCustomContactName('');
      setCustomContactEmail('');
    }
    loadProviders();
    setEditEmailDialogOpen(true);
  };

  const handleCancelEditEmail = () => {
    setEditEmailDialogOpen(false);
    setEditingEmailReminder(null);
    setSelectedProvider('');
    setCustomContactName('');
    setCustomContactEmail('');
  };

  const handleSaveEditEmail = async () => {
    if (!editingEmailReminder) return;
    setIsTogglingEmail(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const payload: any = { email_notification_enabled: true };
      if (selectedProvider) {
        payload.provider_id = selectedProvider;
      } else if (customContactName && customContactEmail) {
        payload.custom_contact_name = customContactName;
        payload.custom_contact_email = customContactEmail;
      } else {
        showMessage({ text: 'Please select a provider or enter custom contact details.' });
        setIsTogglingEmail(false);
        return;
      }
      const response = await fetch(
        `${config.apiUrl}/api/reminders/${editingEmailReminder.id}/toggle-email`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      if (!response.ok) throw new Error('Failed to update email recipient');
      await loadActiveReminders();
      handleCancelEditEmail();
      if (onReminderChange) onReminderChange();
    } catch (err) {
      console.error('Error updating email recipient:', err);
      showMessage({ text: 'Failed to update email recipient. Please try again.' });
    } finally {
      setIsTogglingEmail(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingReminder) return;
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${editingReminder.id}/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          reminder_date: editingDate,
          reminder_days_before: editingDaysBefore,
          reminder_time: editingTime
        })
      });
      if (response.ok) {
        await loadActiveReminders();
        setEditDialogOpen(false);
        setEditingReminder(null);
        onReminderChange?.();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to update reminder');
      }
    } catch (err) {
      showMessage({ text: 'Failed to update reminder. Please try again.' });
      console.error('Error updating reminder:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingReminder(null);
    setEditingDate('');
    setEditingTime('09:30');
    setEditingDaysBefore(21);
    setEditingReminderOption('21');
  };

  const handleDeleteReminder = (reminder: ActiveReminder) => {
    setDeletingReminder(reminder);
    setDeleteDialogOpen(true);
  };

  const handleArchiveReminder = async (reminder: ActiveReminder, restore = false) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminder.id}/${restore ? 'unarchive' : 'archive'}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json().catch(() => ({ success: false }));
      if (response.ok && result.success) {
        await loadActiveReminders();
      } else {
        showMessage({ text: result.error || 'Failed to archive flow' });
      }
    } catch (err) {
      showMessage({ text: err instanceof Error ? err.message : 'Failed to archive flow' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingReminder) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const response = await fetch(`${config.apiUrl}/api/reminders/${deletingReminder.id}/delete`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json().catch(() => ({ success: false, error: 'Invalid response' }));
      if (response.ok && result.success) {
        await loadActiveReminders();
        setDeleteDialogOpen(false);
        setDeletingReminder(null);
        onReminderChange?.();
      } else {
        const errorMsg = result.error || result.message || 'Failed to delete reminder';
        showMessage({ text: errorMsg });
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete reminder';
      console.error('Error deleting reminder:', err);
      showMessage({ text: errorMsg });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddToCalendar = async (reminderId: string) => {
    setAddingToCalendar(prev => ({ ...prev, [reminderId]: true }));
    try {
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/add-to-calendar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success) await loadActiveReminders();
      else showMessage({ text: result.error || 'Could not add the reminder to your calendar.' });
    } catch {
      showMessage({ text: 'Could not add the reminder to your calendar.' });
    } finally {
      setAddingToCalendar(prev => ({ ...prev, [reminderId]: false }));
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingReminder(null);
  };

  const toggleEmailNotification = async (reminder: ActiveReminder, enabled: boolean, providerId?: string, customName?: string, customEmail?: string) => {
    setIsTogglingEmail(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { showMessage({ text: 'Authentication required.' }); return; }
      const body: any = { email_notification_enabled: enabled };
      if (enabled) {
        if (providerId) body.provider_id = providerId;
        else if (customEmail) { body.custom_contact_name = customName; body.custom_contact_email = customEmail; }
      }
      const response = await fetch(`${config.apiUrl}/api/reminders/${reminder.id}/toggle-email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        await loadActiveReminders();
        setEmailDialogOpen(false);
        setEmailReminder(null);
        onReminderChange?.();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to toggle email notification');
      }
    } catch (err) {
      showMessage({ text: 'Failed to update email notification. Please try again.' });
      console.error('Error toggling email:', err);
    } finally {
      setIsTogglingEmail(false);
    }
  };

  const handleSaveEmailSettings = () => {
    if (!emailReminder) return;
    if (selectedProvider) {
      toggleEmailNotification(emailReminder, true, selectedProvider);
    } else if (customContactEmail && customContactName) {
      toggleEmailNotification(emailReminder, true, undefined, customContactName, customContactEmail);
    }
  };

  const handleCancelEmailSettings = () => {
    setEmailDialogOpen(false);
    setEmailReminder(null);
    setSelectedProvider('');
    setCustomContactName('');
    setCustomContactEmail('');
  };

  const renderReminder = (reminder: ActiveReminder) => {
                  const emails = reminderEmails[reminder.id] || [];
                  const isScheduled = emails.length > 0 && emails.some(e => e.scheduler_job_name);
                  // Provider info is required to arrange a visit — drives the header's ON/OFF chip.
                  // Deliberately live (from provider-status, re-fetched on every load), not
                  // "has a provider email ever been created" — otherwise editing/deleting the
                  // provider on /providers wouldn't be reflected here, since a stale ReminderEmail
                  // drafted against the old provider would keep this permanently true.
                  const providerReady = !!providerStatus[reminder.id]?.found;
                  // A provider email exists AND still corresponds to a currently-registered
                  // provider — false once that provider is deleted, even though the historical
                  // ReminderEmail row itself still exists.
                  const providerEmailIsValid = emails.some(e => e.audience === 'provider') && providerStatus[reminder.id]?.found !== false;
                  const categoryColor = getCategoryColor(reminder.certificate_type_category || 'Other');
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const isOverdue = new Date(reminder.reminder_date) < today;

                  const expiryDate = new Date(reminder.expiry_date);

                  return (
                    <Box key={reminder.id} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2.5,
                        p: 1.25,
                        bgcolor: palette.tokens.white,
                        border: `1px solid ${palette.tokens.hairline}`,
                        borderRadius: expandedCopilotIds.has(reminder.id) ? '16px 16px 0 0' : '16px',
                        transition: 'background 0.12s, box-shadow 0.15s',
                        '&:hover': { boxShadow: '0 2px 10px rgba(20,20,19,0.06)' },
                      }}
                    >
                      {/* Big reminder-date block */}
                      <Box
                        onClick={e => { e.stopPropagation(); handleEditReminder(reminder); }}
                        title="Change when this flow starts"
                        sx={{ textAlign: 'center', minWidth: 44, flexShrink: 0, cursor: 'pointer', borderRadius: 1, '&:hover': { opacity: 0.7 } }}
                      >
                        <Typography variant="displaySm" sx={{ fontSize: '1.9rem', fontWeight: 700, color: isOverdue ? palette.tokens.error : palette.tokens.ink, lineHeight: 1, fontVariantNumeric: 'lining-nums' }}>
                          {new Date(reminder.reminder_date).getDate()}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: palette.tokens.mutedSoft, letterSpacing: 0.6, textTransform: 'uppercase', mt: 0.25 }}>
                          {new Date(reminder.reminder_date).toLocaleDateString('en-GB', { month: 'short' })}
                        </Typography>
                      </Box>

                      <Box sx={{ width: '1px', height: 34, bgcolor: palette.tokens.hairline, flexShrink: 0 }} />

                      {/* Category · address, then title */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                          {reminder.certificate_type_category && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: palette.tokens.surfaceCard, borderRadius: '999px', px: 1.25, py: 0.35, flexShrink: 0 }}>
                              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: categoryColor, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: palette.tokens.ink, whiteSpace: 'nowrap' }}>
                                {reminder.certificate_type_category}
                              </Typography>
                            </Box>
                          )}
                          <Typography sx={{ fontSize: '0.75rem', color: palette.tokens.mutedSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {reminder.normalized_address || reminder.property_address}{getFlatDisplay(reminder.flat_number)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: palette.tokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {reminder.certificate_type}
                        </Typography>
                        {reminder.email_notification_enabled && reminder.email_sent_at && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Email sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'success.main', whiteSpace: 'nowrap' }}>
                              Sent to {reminder.recipient_name || reminder.email_sent_to}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Dates + ring — same layout as the certificate rows: Issued/Expires
                          stacked, ring beside them (not below). Click to edit the reminder. */}
                      <Box
                        onClick={e => { e.stopPropagation(); handleEditReminder(reminder); }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, cursor: 'pointer' }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                            {expiryDate < today ? 'Expired' : 'Expires'} {expiryDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </Typography>
                        </Box>
                        <ExpiryRing issueDate={reminder.issue_date} expiryDate={reminder.expiry_date} size={50} />
                      </Box>

                      {/* Action buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        {(() => {
                          const hasEmail = emails.length > 0;
                          const hasRecipient = hasEmail && emails.some(e => e.recipient_email);
                          const pillSx = {
                            minWidth: '58px', height: '30px', borderRadius: '999px',
                            fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' as const,
                          };

                          if (isScheduled) {
                            return null;
                          }
                          {/* No manual "Confirm" step anymore — once a provider exists, the
                              backend schedules stage-1 emails automatically (see
                              _auto_advance_reminder), so a hasEmail+hasRecipient reminder should
                              already be isScheduled by the time it's rendered. */}
                          if (hasEmail && !hasRecipient) {
                            return (
                              <Tooltip title="Expand Copilot Status below to add recipient">
                                <Box sx={{ ...pillSx, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, bgcolor: '#faf0dd', color: '#a67a1a', textTransform: 'none' as const }}>
                                  Add recipient
                                </Box>
                              </Tooltip>
                            );
                          }
                          // No stage-1 emails yet at all (e.g. no provider on file) — the always-
                          // visible Copilot Status flow below is the entry point now, so there's
                          // nothing left for this pill button to do.
                          return null;
                        })()}

                        <Tooltip title="View certificate in dashboard">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/dashboard?cert=${reminder.certificate_id}`); }} sx={{ bgcolor: palette.tokens.surfaceSoft, borderRadius: '10px', p: 1, '&:hover': { bgcolor: palette.tokens.surfaceCard } }}>
                            <ViewIcon sx={{ fontSize: 18, color: palette.tokens.muted }} />
                          </IconButton>
                        </Tooltip>

                        {(renewalStatus[reminder.id]?.reminder_status ?? reminder.status) === 'completed' && (
                          <Tooltip title="Move to archived">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleArchiveReminder(reminder); }} sx={{ bgcolor: palette.tokens.surfaceSoft, borderRadius: '10px', p: 1, '&:hover': { bgcolor: palette.tokens.surfaceCard } }}>
                              <ArchiveIcon sx={{ fontSize: 18, color: palette.tokens.muted }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {reminder.status === 'archived' && (
                          <Tooltip title="Move back to active flows">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleArchiveReminder(reminder, true); }} sx={{ bgcolor: palette.tokens.surfaceSoft, borderRadius: '10px', p: 1, '&:hover': { bgcolor: palette.tokens.surfaceCard } }}>
                              <UnarchiveIcon sx={{ fontSize: 18, color: palette.tokens.muted }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Delete flow">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteReminder(reminder); }} sx={{ bgcolor: '#fdeceb', borderRadius: '10px', p: 1, '&:hover': { bgcolor: '#fbdada' } }}>
                            <DeleteIcon sx={{ fontSize: 18, color: palette.tokens.error }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Always shown — the flow is the entry point into visit scheduling even before any email exists. */}
                    {true && (
                      <Box sx={{
                        bgcolor: palette.tokens.surfaceSoft, borderRadius: '0 0 16px 16px',
                        border: `1px solid ${palette.tokens.hairline}`, borderTop: 'none',
                        overflow: 'hidden',
                      }}>
                        <Box
                          onClick={() => toggleCopilotStatus(reminder.id)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.6, cursor: 'pointer', userSelect: 'none' }}
                        >
                          <Box sx={{ display: 'flex', color: palette.tokens.primary }}><MillosLogo size={18} agent /></Box>
                          <Typography sx={{ color: palette.tokens.ink, fontWeight: 700, fontSize: '0.72rem' }}>
                            Copilot
                          </Typography>
                          {(() => {
                            const emailsSent = outreachSent(emails) || !!availabilityCoordination[reminder.id]?.recommended_date;
                            const completed = ['completed', 'archived'].includes(renewalStatus[reminder.id]?.reminder_status ?? reminder.status);
                            const [label, color] =
                              completed ? ['COMPLETED', palette.tokens.accentTeal]
                              : !assistantEnabled ? ['OFF', palette.tokens.error]
                              : reminder.recipient_mode === 'none' ? ['PAUSED', palette.tokens.warning]
                              : !providerReady ? ['OFF', palette.tokens.error]
                              : reminder.copilot_paused ? ['PAUSED', palette.tokens.warning]
                              : emailsSent ? ['IN PROGRESS', palette.tokens.accentAmber]
                              : ['ON', palette.tokens.success];
                            return (
                              <Chip
                                label={label}
                                size="small"
                                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: color, color: palette.tokens.white }}
                              />
                            );
                          })()}
                          {!providerReady && reminder.recipient_mode !== 'none' && (
                            <Tooltip title="Why is Copilot off?">
                              <IconButton
                                size="small"
                                onClick={e => { e.stopPropagation(); setCopilotOffInfoOpenFor(reminder.id); }}
                                sx={{ p: 0.15, color: palette.tokens.muted, '&:hover': { color: palette.tokens.ink } }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {providerReady && reminder.recipient_mode !== 'none' && !availabilityCoordination[reminder.id]?.recommended_date && (
                            <Typography
                              onClick={e => { e.stopPropagation(); handleSetCopilotPaused(reminder.id, !reminder.copilot_paused); }}
                              sx={{ fontSize: '0.65rem', color: palette.tokens.primary, cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                            >
                              {reminder.copilot_paused ? 'Resume' : 'Pause for this certificate'}
                            </Typography>
                          )}
                          <Box sx={{ flex: 1 }} />
                          {expandedCopilotIds.has(reminder.id)
                            ? <KeyboardArrowUp sx={{ color: palette.tokens.muted, fontSize: 16 }} />
                            : <KeyboardArrowDown sx={{ color: palette.tokens.muted, fontSize: 16 }} />
                          }
                        </Box>
                        <Collapse in={expandedCopilotIds.has(reminder.id)}>
                          {reminder.recipient_mode === 'none' ? (
                            <Box sx={{ px: 1.5, py: 2 }}>
                              <Typography sx={{ fontSize: '0.75rem', color: palette.tokens.muted, fontWeight: 600 }}>
                                The AI assistant is paused for this certificate type, so no emails are scheduled. Click on Personalize to change this setting.
                              </Typography>
                            </Box>
                          ) : (() => {
                            const t = palette.tokens;
                            const stage1 = emails.filter(e => e.audience === 'provider' || e.audience === 'tenant');
                            const stage1Sent = outreachSent(emails);
                            const contactsOn = functionOn(assistantFunctions, 'contacts');
                            const bookingOn = functionOn(assistantFunctions, 'booking');
                            // Emails already sent stay shown even if Contact is switched off later.
                            const contactsReal = contactsOn || stage1Sent;
                            const hasEvent = !!reminder.google_event_id;
                            const fmtLong = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
                            const calendarDateLabel = fmtLong(new Date(reminder.reminder_date));
                            const scheduledDateOnly = stage1.find(e => e.scheduled_send_date)?.scheduled_send_date;
                            const outreachDateLabel = fmtLong(scheduledDateOnly ? new Date(scheduledDateOnly + 'T00:00:00') : new Date(reminder.reminder_date));
                            const coordination = availabilityCoordination[reminder.id];
                            const responses = availabilityResponses[reminder.id] || [];
                            const coordinationSucceeded = !!coordination?.recommended_date;
                            const coordinationFailed = !!coordination && !coordination.recommended_date;
                            const confirmed = coordinationSucceeded && !!(coordination.tenant_confirmation_email_id || coordination.provider_confirmation_email_id);
                            const hasTenantEmail = emails.some(e => e.audience === 'tenant');
                            const providerResponse = responses.find((r: any) => r.audience === 'provider');
                            const confirmationEmails = emails
                              .filter(e => e.audience === 'tenant_confirmation' || e.audience === 'provider_confirmation' || e.audience === 'landlord_confirmation')
                              .sort((a, b) => {
                                const order: Record<string, number> = { tenant_confirmation: 0, provider_confirmation: 1, landlord_confirmation: 2 };
                                return (order[a.audience] ?? 3) - (order[b.audience] ?? 3);
                              });

                            const linkSx = { color: t.primary, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.7rem', fontWeight: 600 };
                            const bodySx = { fontSize: '0.72rem', color: t.muted, lineHeight: 1.45 };
                            const partyChip = (key: string, role: string, name: string, extras?: React.ReactNode) => (
                              <Box key={key} sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                bgcolor: t.surfaceCard, border: `1px solid ${t.hairline}`, borderRadius: '999px', pl: 1, pr: 0.5, py: 0.25,
                              }}>
                                <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: t.primaryActive, textTransform: 'uppercase' }}>{role}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: t.ink, fontWeight: 600 }}>{name}</Typography>
                                {extras}
                              </Box>
                            );
                            const emailChip = (email: any) => {
                              const isTenant = email.audience === 'tenant';
                              const who = isTenant
                                ? (email.tenant_name || 'Tenant')
                                : (providerStatus[reminder.id]?.provider?.provider_name || email.provider_name || reminder.recipient_name || email.recipient_email || 'Provider');
                              return partyChip(email.id, isTenant ? 'Tenant' : 'Provider', who, (
                                <>
                                  {sentInfoIcon(email, (() => {
                                    const resp = responses.find((r: any) => r.email_id === email.id);
                                    const label = email.audience === 'provider' ? 'Availability offered:' : email.audience === 'tenant' ? 'Availability picked:' : "Dates picked:";
                                    return { label, repliedAt: resp?.submitted_at, lines: (resp?.submitted_slots || []).map((s: any) => `${new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${s.start_time}–${s.end_time}`) };
                                  })())}
                                  {email.status !== 'sent' && (
                                    <Tooltip title="View / edit email draft">
                                      <IconButton size="small" onClick={e => { e.stopPropagation(); handleViewEmail(reminder, email.id); }} sx={{ p: 0.25, color: t.muted, '&:hover': { color: t.ink } }}>
                                        <Email sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              ));
                            };

                            // Provider and tenant chips, with "not found" actions when someone is missing.
                            const providerNodes: React.ReactNode[] = [];
                            const tenantNodes: React.ReactNode[] = [];
                            const tenantEmail = emails.find(e => e.audience === 'tenant');
                            const providerEmail = emails.find(e => e.audience === 'provider' && providerEmailIsValid);
                            const tenantSelf = !tenantEmail && tenantStatus[reminder.id]?.tenant_notify_mode === 'self';
                            if (tenantEmail) {
                              tenantNodes.push(emailChip(tenantEmail));
                            } else if (reminder.recipient_mode !== 'provider') {
                              const status = tenantStatus[reminder.id];
                              if (status?.found && status.tenant) {
                                tenantNodes.push(partyChip('tenant-found', 'Tenant', status.tenant.name));
                              } else if (tenantSelf) {
                                tenantNodes.push(
                                  <Typography key="tenant-self" sx={bodySx}>
                                    You're notifying the tenant ·{' '}
                                    <Box component="span" onClick={e => { e.stopPropagation(); handleSetTenantNotifyMode(reminder.id, null); }} sx={linkSx}>Undo</Box>
                                  </Typography>
                                );
                              } else {
                                tenantNodes.push(
                                  <Box key="tenant-missing" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    {partyChip('tenant-nf', 'Tenant', 'not found')}
                                    <Box component="span" onClick={e => { e.stopPropagation(); setAddTenantOpenFor(reminder.id); setAddTenantName(''); setAddTenantEmail(''); }} sx={linkSx}>Add tenant</Box>
                                    <Box component="span" onClick={e => { e.stopPropagation(); handleSetTenantNotifyMode(reminder.id, 'self'); }} sx={linkSx}>I'll notify the tenant myself</Box>
                                  </Box>
                                );
                              }
                            }
                            if (providerEmail) {
                              providerNodes.push(emailChip(providerEmail));
                            } else if (reminder.recipient_mode !== 'tenant') {
                              const status = providerStatus[reminder.id];
                              if (status?.found && status.provider) {
                                providerNodes.push(partyChip('provider-found', 'Provider', status.provider.provider_name));
                              } else {
                                providerNodes.push(
                                  <Box key="provider-missing" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    {partyChip('provider-nf', 'Provider', 'not found')}
                                    <Box component="span" onClick={e => { e.stopPropagation(); setAddProviderOpenFor(reminder.id); setAddProviderName(''); setAddProviderEmail(''); setSelectedExistingProviderId(''); setAddProviderConflict(null); loadProviders(); }} sx={linkSx}>Add provider</Box>
                                  </Box>
                                );
                              }
                            }

                            type StepState = 'done' | 'pending' | 'off' | 'error';
                            const nodeColor: Record<StepState, string> = { done: t.success, pending: t.primary, off: t.mutedSoft, error: t.error };
                            const nodeGlyph: Record<StepState, string> = { done: '✓', pending: '', off: '–', error: '!' };
                            let pulseUsed = false; // only the first pending step marks where the flow is
                            const renderStep = (state: StepState, title: string, body: React.ReactNode, last = false, description?: React.ReactNode, action?: React.ReactNode, pulseNow = false) => {
                              const ahead = pulseUsed;
                              const pulse = pulseNow || (state === 'pending' && !pulseUsed);
                              if (pulse) pulseUsed = true;
                              return (
                              <Box sx={{ display: 'flex', gap: 1.5, opacity: ahead ? 0.45 : 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                  <Box sx={{
                                    width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.65rem', fontWeight: 700, lineHeight: 1,
                                    bgcolor: state === 'pending' ? t.white : nodeColor[state],
                                    border: `2px solid ${nodeColor[state]}`, color: t.white,
                                    ...(pulse && {
                                      animation: 'flowPulse 1.6s ease-out infinite',
                                      '@keyframes flowPulse': {
                                        '0%': { boxShadow: `0 0 0 0 ${nodeColor[state]}99` },
                                        '70%': { boxShadow: `0 0 0 8px ${nodeColor[state]}00` },
                                        '100%': { boxShadow: `0 0 0 0 ${nodeColor[state]}00` },
                                      },
                                    }),
                                  }}>{nodeGlyph[state]}</Box>
                                  {!last && <Box sx={{ flex: 1, width: '2px', bgcolor: t.hairline, mt: 0.5 }} />}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Tooltip title={description || ''} placement="right" arrow disableHoverListener={!description}>
                                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: state === 'off' ? t.muted : t.ink, lineHeight: 1.3, width: 'fit-content', cursor: description ? 'help' : 'default' }}>{title}</Typography>
                                    </Tooltip>
                                    {action}
                                  </Box>
                                  {body && <Box sx={{ mt: 0.5 }}>{body}</Box>}
                                </Box>
                              </Box>
                              );
                            };

                            // Step 1: Calendar
                            const calendarStep = renderStep(
                              hasEvent ? 'done' : 'off',
                              hasEvent ? 'Reminder added to your calendar' : 'No reminder in your calendar',
                              hasEvent ? null : (
                                <Typography sx={bodySx}>
                                  <Box component="span" onClick={e => { e.stopPropagation(); if (!addingToCalendar[reminder.id]) handleAddToCalendar(reminder.id); }} sx={linkSx}>
                                    {addingToCalendar[reminder.id] ? 'Setting...' : 'Set it instead'}
                                  </Box>
                                </Typography>
                              ),
                              false,
                              hasEvent ? `Scheduled for ${calendarDateLabel}.` : `I have not set a reminder in your calendar for ${calendarDateLabel}.`
                            );

                            // Steps 2 and 3: the provider is emailed first, the tenant once the provider has replied.
                            const providerSent = providerEmail?.status === 'sent';
                            const tenantSent = tenantEmail?.status === 'sent';
                            const providerReplied = !!providerResponse;
                            const tenantReplied = responses.some((r: any) => r.audience === 'tenant');
                            const flowPaused = !!reminder.copilot_paused;
                            const shortSlot = (s: any) => `${new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${s.start_time}–${s.end_time}`;
                            const sendNowFor = (email: any, tooltip: string) => email && email.status !== 'sent' && (
                              flowPaused ? (
                                <Typography sx={{ fontSize: '0.72rem', color: t.warning, fontWeight: 600 }}>
                                  This flow is paused, nothing will be sent on {outreachDateLabel}.
                                </Typography>
                              ) : (
                                <Tooltip title={tooltip}>
                                  <Button
                                    size="small"
                                    onClick={e => { e.stopPropagation(); handleSendNow(reminder.id); }}
                                    disabled={sendingNow[reminder.id]}
                                    sx={{ height: 22, minWidth: 0, fontSize: '0.62rem', fontWeight: 700, px: 1.25, whiteSpace: 'nowrap', bgcolor: t.primary, color: t.onPrimary, '&:hover': { bgcolor: t.primaryActive }, '&:disabled': { opacity: 0.5 } }}
                                  >
                                    {sendingNow[reminder.id] ? 'Sending...' : 'Send now'}
                                  </Button>
                                </Tooltip>
                              )
                            );
                            const chaseControl = (email: any) => {
                              const c = email?.chase;
                              if (!c?.awaiting || flowPaused) return null;
                              const when = c.chase_count > 0 ? null
                                : c.days_until_chase === 0 ? 'Chasing today'
                                : `Chasing in ${c.days_until_chase} day${c.days_until_chase === 1 ? '' : 's'}`;
                              return (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                  {when && <Typography sx={{ fontSize: '0.72rem', color: t.muted, fontWeight: 600 }}>{when}</Typography>}
                                  <Tooltip title="Send a follow-up email now">
                                    <Button
                                      size="small"
                                      onClick={e => { e.stopPropagation(); handleChaseNow(reminder, email.id); }}
                                      disabled={chasingEmail[email.id]}
                                      sx={{ height: 22, minWidth: 0, fontSize: '0.62rem', fontWeight: 700, px: 1.25, whiteSpace: 'nowrap', bgcolor: t.primary, color: t.onPrimary, '&:hover': { bgcolor: t.primaryActive }, '&:disabled': { opacity: 0.5 } }}
                                    >
                                      {chasingEmail[email.id] ? 'Sending...' : 'Chase now'}
                                    </Button>
                                  </Tooltip>
                                </Box>
                              );
                            };
                            const wasChased = (email: any) => !!email?.chase?.awaiting && email.chase.chase_count > 0;
                            const providerChased = wasChased(providerEmail);
                            const tenantChased = wasChased(tenantEmail);
                            const providerStep = !contactsReal ? renderStep(
                              'off', 'Reaching out on your own',
                              null, false, 'You chose to reach out to the parties on your own.'
                            ) : reminder.recipient_mode === 'tenant' ? null : renderStep(
                              providerReplied ? 'done' : !providerReady ? 'error' : providerChased ? 'done' : 'pending',
                              providerReplied ? 'Provider replied with availability'
                                : providerChased ? 'Provider emailed'
                                : providerSent ? "Waiting for the provider's availability"
                                : flowPaused ? 'Provider email paused'
                                : `Provider email scheduled for ${outreachDateLabel}`,
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                                {providerNodes.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>{providerNodes}</Box>}
                                {!providerReady && (
                                  <Typography sx={{ ...bodySx, color: t.error }}>
                                    I don't have enough info to arrange this until at least the provider info is provided.
                                  </Typography>
                                )}
                              </Box>,
                              false,
                              providerReplied
                                ? `Offered: ${(providerResponse.submitted_slots || []).map(shortSlot).join(' · ')}`
                                : providerSent
                                ? 'I emailed the provider and am waiting for their availability.'
                                : reminder.recipient_mode === 'provider'
                                ? `On ${outreachDateLabel} I will email the provider for this renewal.`
                                : `On ${outreachDateLabel} I will email the provider for their availability. Once they reply I will email the tenant their options.`,
                              sendNowFor(providerEmail, 'Email the provider now') || (!providerChased && chaseControl(providerEmail))
                            );
                            const providerChaseStep = providerChased && !providerReplied ? renderStep(
                              'done', 'Provider chased, waiting for response', null, false,
                              'The provider had not replied, so I sent a follow-up email.',
                              null, true
                            ) : null;
                            const tenantStep = !contactsReal || reminder.recipient_mode === 'provider' ? null : renderStep(
                              tenantReplied ? 'done' : tenantSelf ? 'off' : tenantChased ? 'done' : 'pending',
                              tenantReplied ? 'Tenant replied'
                                : tenantSelf ? 'You are notifying the tenant'
                                : tenantChased ? 'Tenant emailed'
                                : tenantSent ? "Waiting for the tenant's reply"
                                : providerEmail ? 'Tenant email'
                                : flowPaused ? 'Tenant email paused'
                                : `Tenant email scheduled for ${outreachDateLabel}`,
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                                {tenantNodes.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>{tenantNodes}</Box>}
                                {!providerEmail && sendNowFor(tenantEmail, 'Email the tenant now')}
                              </Box>,
                              false,
                              tenantSent && !tenantReplied
                                ? "I emailed the tenant the provider's options. Waiting for them to pick one or propose other dates."
                                : !tenantSent && !tenantReplied && !tenantSelf && providerEmail
                                ? 'When the provider replies I will email the tenant their options, with buttons to pick one or propose other dates.'
                                : undefined,
                              !tenantChased && !tenantReplied ? chaseControl(tenantEmail) : null
                            );
                            const tenantChaseStep = tenantChased && !tenantReplied ? renderStep(
                              'done', 'Tenant chased, waiting for response', null, false,
                              'The tenant had not replied, so I sent a follow-up email.',
                              null, true
                            ) : null;

                            // None of the provider's times suited the tenant: the provider is asked about the tenant's dates once.
                            const counterEmail = emails.find(e => e.audience === 'provider_counter');
                            const counterResponse = responses.find((r: any) => r.audience === 'provider_counter');
                            const counterPicked = !!counterResponse && (counterResponse.submitted_slots || []).length > 0;
                            const counterChased = !counterResponse && wasChased(counterEmail);
                            const counterStep = !counterEmail ? null : renderStep(
                              counterResponse ? (counterPicked ? 'done' : 'error') : counterChased ? 'done' : 'pending',
                              counterResponse
                                ? (counterPicked ? "Provider chose from the tenant's dates" : "Provider could not make the tenant's dates")
                                : counterChased ? "Provider emailed the tenant's dates"
                                : "Waiting for the provider to review the tenant's dates",
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>{emailChip(counterEmail)}</Box>,
                              false,
                              "None of the provider's times suited the tenant, so I emailed the provider the dates the tenant can do.",
                              !counterChased && !counterResponse ? chaseControl(counterEmail) : null
                            );
                            const counterChaseStep = counterChased ? renderStep(
                              'done', 'Provider chased, waiting for response', null, false,
                              "The provider had not replied about the tenant's dates, so I sent a follow-up email.",
                              null, true
                            ) : null;

                            // Step 3: Availability and appointment
                            const slotChips = (providerResponse?.submitted_slots || [])
                              .slice()
                              .sort((a: any, b: any) => a.date.localeCompare(b.date))
                              .map((slot: any, idx: number) => (
                                <Chip
                                  key={idx}
                                  label={`${new Date(slot.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}, ${slot.start_time}–${slot.end_time}`}
                                  size="small" clickable
                                  disabled={!!confirmingSlot[reminder.id]}
                                  onClick={e => { e.stopPropagation(); handleConfirmVisitDate(reminder.id, slot); }}
                                  sx={{ fontSize: '0.68rem', fontWeight: 600, bgcolor: t.accentTeal, color: t.white, '&:hover': { bgcolor: t.primary } }}
                                />
                              ));
                            let appointmentState: StepState = 'pending';
                            let appointmentTitle = bookingOn ? 'Book the appointment' : 'Propose a date';
                            let appointmentBody: React.ReactNode;
                            let appointmentDescription: React.ReactNode;
                            if (coordinationSucceeded) {
                              appointmentState = 'done';
                              appointmentTitle = confirmed ? 'Slot found and booked' : 'Date proposed';
                              appointmentBody = (
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: t.ink }}>
                                      {new Date(coordination.recommended_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} ({coordination.recommended_start_time}–{coordination.recommended_end_time})
                                    </Typography>
                                    <Tooltip title="See what the provider and tenant said">
                                      <IconButton size="small" onClick={e => { e.stopPropagation(); setCoordinationDetailsOpenFor(reminder.id); }} sx={{ p: 0.25, color: t.muted, '&:hover': { color: t.ink } }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              );
                              appointmentDescription = confirmed ? 'The confirmation emails went to everyone involved.' : 'You chose to book the appointment yourself, so no confirmation emails are sent.';
                            } else if (coordinationFailed) {
                              appointmentState = 'error';
                              appointmentTitle = 'No agreement reached';
                              appointmentBody = (
                                <Typography sx={bodySx}>
                                  {coordination.ai_reasoning || 'No overlapping availability was found.'}{' '}
                                  <Box component="span" onClick={e => { e.stopPropagation(); setCoordinationDetailsOpenFor(reminder.id); }} sx={linkSx}>See why</Box>.{' '}
                                  You need to decide what to do next.
                                </Typography>
                              );
                              appointmentDescription = undefined;
                            } else if (counterEmail) {
                              appointmentTitle = bookingOn ? 'Book the appointment' : 'Propose a date';
                              appointmentDescription = counterResponse
                                ? "The provider picked from the tenant's dates. I am working out the best date."
                                : "I emailed the provider the tenant's dates. If none work, the flow ends and you decide what to do next.";
                            } else if (!contactsReal) {
                              appointmentTitle = 'Enter the availability';
                              appointmentDescription = bookingOn
                                ? 'Enter when each party is available. I will find the best slot, book it and send the confirmation emails.'
                                : 'Enter when each party is available and I will propose a date.';
                            } else if (!stage1Sent) {
                              appointmentTitle = bookingOn ? 'Book the appointment' : 'Propose a date';
                              appointmentDescription = bookingOn
                                ? 'Once the emails are sent I will wait for the availability, then book the appointment.'
                                : 'Once the availability is in I will propose a date. You book the appointment yourself.';
                            } else if (!hasTenantEmail) {
                              appointmentTitle = providerResponse ? 'Pick the date that works best' : (bookingOn ? 'Book the appointment' : 'Propose a date');
                              appointmentBody = providerResponse ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                                  {slotChips}
                                  {providerResponse.notes && <Typography sx={{ ...bodySx, fontStyle: 'italic' }}>{providerResponse.who} note: {providerResponse.notes}</Typography>}
                                </Box>
                              ) : null;
                              if (!providerResponse) appointmentDescription = 'I will confirm the date with the provider once they reply.';
                            } else {
                              const providerReplied = responses.some((r: any) => r.audience === 'provider');
                              const tenantReplied = responses.some((r: any) => r.audience === 'tenant');
                              const hasProviderEmail = emails.some(e => e.audience === 'provider');
                              const tenantEmailSent = emails.some(e => e.audience === 'tenant' && e.status === 'sent');
                              let text: string;
                              if (providerReplied && tenantReplied) {
                                text = 'Both replies are in, I am working out the best date.';
                              } else if (hasProviderEmail && !providerReplied) {
                                text = 'Once the provider replies I will email the tenant their options.';
                              } else if (hasProviderEmail && !tenantEmailSent) {
                                text = reminder.copilot_paused
                                  ? "The flow is paused. When you resume I will email the tenant the provider's options."
                                  : 'The provider replied. I am emailing the tenant their options.';
                              } else {
                                text = hasProviderEmail
                                  ? 'I emailed the tenant the provider\'s options. They can press one of them or propose other dates.'
                                  : 'Waiting for the tenant to reply.';
                              }
                              appointmentBody = null;
                              appointmentDescription = text;
                            }
                            const appointmentStep = renderStep(
                              appointmentState, appointmentTitle,
                              <>
                                {appointmentBody}
                                {!contactsReal && (
                                  <ManualBookingBoxes
                                    reminderId={reminder.id}
                                    expiryDate={reminder.expiry_date}
                                    responses={responses}
                                    onChanged={() => loadReminderEmails([reminder])}
                                  />
                                )}
                              </>,
                              coordinationFailed,
                              appointmentDescription
                            );

                            // Step 4: Confirmations — nothing to confirm when the flow ended without agreement.
                            const confirmationStep = coordinationFailed ? null : renderStep(
                              confirmed ? 'done' : !bookingOn ? 'off' : 'pending',
                              confirmed ? 'Confirmations sent' : !bookingOn ? 'Confirmations not sent' : 'Confirmations',
                              confirmed ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {confirmationEmails.map(email => {
                                    const isTenant = email.audience === 'tenant_confirmation';
                                    const isLandlord = email.audience === 'landlord_confirmation';
                                    const who = isTenant ? (email.tenant_name || 'Tenant') : isLandlord ? (user?.name || 'You') : (email.provider_name || reminder.recipient_name || 'Provider');
                                    return partyChip(email.id, isTenant ? 'Tenant' : isLandlord ? 'You' : 'Provider', who, sentInfoIcon(email));
                                  })}
                                </Box>
                              ) : null,
                              false,
                              confirmed ? undefined : !bookingOn
                                ? 'You chose to book the appointment yourself, so no confirmation emails are sent.'
                                : 'Once the appointment is booked I will email the tenant, the provider and you.'
                            );

                            // Step 5: the renewed certificate arrives in your inbox and you approve it in Approvals.
                            const renewalDone = ['completed', 'archived'].includes(renewalStatus[reminder.id]?.reminder_status ?? reminder.status);
                            const pendingRenewal = renewalStatus[reminder.id]?.pending_renewal;
                            const renewalStep = coordinationFailed ? null : renderStep(
                              renewalDone ? 'done' : 'pending',
                              renewalDone ? 'Certificate renewed' : pendingRenewal ? 'Waiting for your approval' : 'Renew the certificate',
                              null,
                              true,
                              renewalDone
                                ? 'You approved the renewed certificate and it replaced the old one.'
                                : pendingRenewal
                                ? 'The renewed certificate has arrived. Approve it in Approvals to complete this flow.'
                                : 'Once the provider emails the renewed certificate to your inbox, I will match it and ask for your approval.',
                              pendingRenewal && !renewalDone ? (
                                <Box component="span" onClick={e => { e.stopPropagation(); navigate('/approvals'); }} sx={linkSx}>Go to Approvals</Box>
                              ) : null
                            );

                            return (
                              <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                                {calendarStep}
                                {providerStep}
                                {providerChaseStep}
                                {tenantStep}
                                {tenantChaseStep}
                                {counterStep}
                                {counterChaseStep}
                                {appointmentStep}
                                {confirmationStep}
                                {renewalStep}
                              </Box>
                            );
                          })()}
                        </Collapse>
                      </Box>
                    )}
                    </Box>
                  );
  };

  // Completed flows stay in the main list until the user archives them
  const activeList = reminders.filter(r => r.status !== 'archived');
  const archivedList = reminders
    .filter(r => r.status === 'archived')
    .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
  const grouped: Record<string, ActiveReminder[]> = {};
  activeList.forEach(r => {
    const year = new Date(r.expiry_date).getFullYear().toString();
    if (!grouped[year]) grouped[year] = [];
    grouped[year].push(r);
  });
  const sortedYears = Object.keys(grouped).sort();

  if (loading && reminders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: palette.tokens.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Typography variant="displaySm" component="h3" sx={{ fontSize: '2rem', fontWeight: 700, color: palette.tokens.ink, whiteSpace: 'nowrap' }}>
            Flows
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: palette.tokens.hairline }} />
        </Box>
      </Box>

      {activeList.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontSize: '0.9rem', color: palette.tokens.mutedSoft }}>
            No active reminders set. Select a certificate and click the bell icon to create a reminder.
          </Typography>
        </Box>
      ) : (
        sortedYears.map(year => {
          const yearReminders = grouped[year];
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const in7 = new Date(today); in7.setDate(in7.getDate() + 7);
          let yearOverdue = 0, yearDueSoon = 0;
          yearReminders.forEach(r => {
            const rd = new Date(r.reminder_date);
            if (rd < today) yearOverdue++;
            else if (rd <= in7) yearDueSoon++;
          });

          // Group by reminder month — the "flats" of this year
          const byMonth: Record<string, ActiveReminder[]> = {};
          yearReminders.forEach(r => {
            const d = new Date(r.reminder_date);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (!byMonth[key]) byMonth[key] = [];
            byMonth[key].push(r);
          });
          const sortedMonths = Object.keys(byMonth).sort();
          const selectedMonth = (selectedMonths[year] && sortedMonths.includes(selectedMonths[year]))
            ? selectedMonths[year]
            : sortedMonths[0];
          const monthReminders = byMonth[selectedMonth] || [];

          return (
          <Box key={year} sx={{ mb: 4 }}>
            {/* Year header */}
            <Box
              onClick={() => toggleYear(year)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, cursor: 'pointer', userSelect: 'none', flexWrap: 'wrap' }}
            >
              <Typography sx={{
                fontSize: '2rem', fontWeight: 900, color: palette.tokens.primary,
                letterSpacing: -1, lineHeight: 1,
              }}>
                {year}
              </Typography>
              {yearOverdue > 0 && (
                <Box sx={{ bgcolor: palette.tokens.error, color: palette.tokens.white, borderRadius: 10, px: 1.25, py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>
                  {yearOverdue} overdue
                </Box>
              )}
              {yearDueSoon > 0 && (
                <Box sx={{ bgcolor: palette.tokens.warning, color: palette.tokens.white, borderRadius: 10, px: 1.25, py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>
                  {yearDueSoon} due soon
                </Box>
              )}
              <Box sx={{ flex: 1, height: '1px', bgcolor: palette.tokens.hairline }} />
              {expandedYears.has(year)
                ? <KeyboardArrowUp sx={{ color: palette.tokens.mutedSoft, fontSize: 20 }} />
                : <KeyboardArrowDown sx={{ color: palette.tokens.mutedSoft, fontSize: 20 }} />
              }
            </Box>

            <Collapse in={expandedYears.has(year)}>
              {(() => {
                return (
                  <>
                    {/* Month tabs — one per month with reminders this year, mirrors PropertyCard's unit tabs */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0 }}>
                      {sortedMonths.map(monthKey => {
                        const tabReminders = byMonth[monthKey];
                        const [yr, mo] = monthKey.split('-');
                        const tabLabel = new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }).toUpperCase();
                        const tabOverdue = tabReminders.some(r => new Date(r.reminder_date) < today);
                        const tabDueSoon = !tabOverdue && tabReminders.some(r => new Date(r.reminder_date) <= in7);
                        const dotColor = tabOverdue ? palette.tokens.error : tabDueSoon ? palette.tokens.warning : palette.tokens.success;
                        const isSelected = monthKey === selectedMonth;
                        return (
                          <Box
                            key={monthKey}
                            onClick={(e) => { e.stopPropagation(); setSelectedMonths(prev => ({ ...prev, [year]: monthKey })); }}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 0.75,
                              px: 1.5, py: 0.75, borderRadius: '10px 10px 0 0',
                              bgcolor: isSelected ? palette.tokens.white : 'transparent',
                              border: isSelected ? `1px solid ${palette.tokens.hairline}` : '1px solid transparent',
                              borderBottom: isSelected ? `1px solid ${palette.tokens.white}` : 'none',
                              mb: isSelected ? '-1px' : 0,
                              cursor: 'pointer', position: 'relative', zIndex: isSelected ? 1 : 0,
                              transition: 'background-color 0.15s ease',
                              '&:hover': !isSelected ? { bgcolor: 'rgba(255,255,255,0.5)' } : {},
                            }}
                          >
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? palette.tokens.ink : palette.tokens.muted, whiteSpace: 'nowrap' }}>
                              {tabLabel} · {tabReminders.length}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Content panel — only the selected month's reminders */}
                    <Box sx={{ bgcolor: palette.tokens.canvas, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '0 12px 12px 12px', p: 2 }}>
                      {monthReminders.map(renderReminder)}
                          </Box>
                  </>
                );
              })()}
            </Collapse>
          </Box>
          );
        })
      )}

      {archivedList.length > 0 && (
        <Box sx={{ mt: 2, mb: 4 }}>
          <Box
            onClick={() => setArchivedOpen(o => !o)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, cursor: 'pointer', userSelect: 'none' }}
          >
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: palette.tokens.muted, letterSpacing: -0.5, lineHeight: 1 }}>
              Archived
            </Typography>
            <Box sx={{ bgcolor: palette.tokens.surfaceCard, color: palette.tokens.muted, borderRadius: 10, px: 1.25, py: 0.3, fontSize: '0.75rem', fontWeight: 700 }}>
              {archivedList.length}
            </Box>
            <Box sx={{ flex: 1, height: '1px', bgcolor: palette.tokens.hairline }} />
            {archivedOpen
              ? <KeyboardArrowUp sx={{ color: palette.tokens.mutedSoft, fontSize: 20 }} />
              : <KeyboardArrowDown sx={{ color: palette.tokens.mutedSoft, fontSize: 20 }} />}
          </Box>
          <Collapse in={archivedOpen}>
            <Box sx={{ bgcolor: palette.tokens.canvas, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '12px', p: 2 }}>
              {archivedList.map(renderReminder)}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Edit Reminder Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, fontSize: '1.5rem' }}>
          Edit Reminder
        </DialogTitle>
        <DialogContent>
          {editingReminder && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                Certificate expires on: <strong>{editingReminder.expiry_date ? new Date(editingReminder.expiry_date).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }) : 'N/A'}</strong>
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Remind me before expiry</InputLabel>
                <Select
                  value={editingReminderOption}
                  label="Remind me before expiry"
                  onChange={(e) => {
                    const option = e.target.value;
                    setEditingReminderOption(option);
                    if (option === '14') {
                      setEditingDaysBefore(14);
                      if (editingReminder.expiry_date) {
                        const d = new Date(editingReminder.expiry_date);
                        d.setDate(d.getDate() - 14);
                        setEditingDate(d.toISOString().split('T')[0]);
                      }
                    } else if (option === '21') {
                      setEditingDaysBefore(21);
                      if (editingReminder.expiry_date) {
                        const d = new Date(editingReminder.expiry_date);
                        d.setDate(d.getDate() - 21);
                        setEditingDate(d.toISOString().split('T')[0]);
                      }
                    }
                  }}
                >
                  <MenuItem value="14">2 Weeks</MenuItem>
                  <MenuItem value="21">3 Weeks</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>

              {editingReminderOption === 'custom' && (
                <TextField
                  fullWidth
                  label="Custom Reminder Date"
                  type="date"
                  value={editingDate}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    setEditingDate(selectedDate);
                    if (editingReminder.expiry_date && selectedDate) {
                      const expiry = new Date(editingReminder.expiry_date);
                      const reminder = new Date(selectedDate);
                      const diffDays = Math.ceil((expiry.getTime() - reminder.getTime()) / (1000 * 60 * 60 * 24));
                      setEditingDaysBefore(diffDays > 0 ? diffDays : 1);
                    }
                  }}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split('T')[0], max: editingReminder.expiry_date }}
                />
              )}

              <TextField
                fullWidth
                label="Reminder Time"
                type="time"
                value={editingTime}
                onChange={(e) => setEditingTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Calendar event and email will be scheduled at this time"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={isUpdating || !editingDate} startIcon={isUpdating ? <CircularProgress size={16} /> : null}>
            {isUpdating ? 'Updating...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Flow</DialogTitle>
        <DialogContent>
          {deletingReminder && (() => {
            const emails = reminderEmails[deletingReminder.id] || [];
            const coordination = availabilityCoordination[deletingReminder.id];
            const stage1Sent = outreachSent(emails);
            const confirmed = !!coordination && !!(coordination.tenant_confirmation_email_id || coordination.provider_confirmation_email_id);
            const progress = deletingReminder.status === 'completed'
              ? 'Certificate renewed (flow complete).'
              : confirmed
              ? 'Appointment booked and confirmation emails sent (flow complete).'
              : coordination?.recommended_date
                ? 'A date has been proposed; no confirmation emails were sent.'
                : coordination
                  ? 'Availability received, but no date works for both parties.'
                  : stage1Sent
                    ? 'Provider emailed; waiting for availability replies.'
                    : emails.some(e => e.audience === 'provider' || e.audience === 'tenant')
                      ? 'Email drafts created; nothing sent yet.'
                      : 'Flow not started; no emails drafted yet.';
            return (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete this flow?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>{deletingReminder.certificate_title?.replace(/\.(pdf|PDF)$/, '') || 'Untitled Certificate'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Reminder date: {new Date(deletingReminder.reminder_date).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Currently at:</strong> {progress}
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This permanently deletes the flow: its reminder, Google Calendar event, email drafts and scheduled sends, and any availability and proposed or booked date. Emails already sent cannot be recalled. This cannot be undone.
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={isDeleting} startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {isDeleting ? 'Deleting...' : 'Delete Flow'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Tenant Dialog — inline from the reminder card's "Tenant not found" state. Saves via
          the same Tenant/Tenancy tables /properties uses, mirroring the Tenancy dialog's minimum
          fields (Name, Email), with the flat's address prefilled read-only for context. */}
      <Dialog open={!!addTenantOpenFor} onClose={() => setAddTenantOpenFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Tenant</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In order to notify the tenant of this flat you need to provide who the tenant is.
            </Typography>
            {addTenantOpenFor && tenantStatus[addTenantOpenFor]?.prefill_address && (
              <TextField
                label="Address"
                value={tenantStatus[addTenantOpenFor].prefill_address}
                fullWidth
                disabled
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label="Name"
              value={addTenantName}
              onChange={e => setAddTenantName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email"
              value={addTenantEmail}
              onChange={e => setAddTenantEmail(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTenantOpenFor(null)}>Cancel</Button>
          <Button
            onClick={() => addTenantOpenFor && handleAddTenant(addTenantOpenFor)}
            variant="contained"
            disabled={savingTenant}
            startIcon={savingTenant ? <CircularProgress size={16} /> : undefined}
          >
            {savingTenant ? 'Saving...' : 'Add tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Provider Dialog — inline from the reminder card's "Provider not found" state. Saves
          via the same CertificateProvider table /providers reads, scoped to this reminder's own
          certificate_type_category (fixed, not user-editable — the whole point is this provider
          covers exactly the cert type this reminder is about). */}
      <Dialog
        open={!!addProviderOpenFor}
        onClose={() => { setAddProviderOpenFor(null); setAddProviderConflict(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Provider</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              In order to notify a provider for this certificate you need to add one — or pick one
              you've already registered for a different certificate type.
            </Typography>
            {addProviderOpenFor && providerStatus[addProviderOpenFor]?.certificate_type_category && (
              <TextField
                label="Certificate type"
                value={providerStatus[addProviderOpenFor].certificate_type_category}
                fullWidth
                disabled
                sx={{ mb: 2 }}
              />
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Existing provider</InputLabel>
              <Select
                value={selectedExistingProviderId}
                label="Existing provider"
                onChange={e => { setSelectedExistingProviderId(e.target.value as string); setAddProviderConflict(null); }}
              >
                <MenuItem value=""><em>None — add a new provider below</em></MenuItem>
                {providers.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.provider_name} ({p.provider_email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {addProviderConflict && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedExistingProviderId(addProviderConflict.existingProvider.id);
                      setAddProviderConflict(null);
                    }}
                  >
                    Select it
                  </Button>
                }
              >
                {addProviderConflict.message}
              </Alert>
            )}

            {!selectedExistingProviderId && (
              <>
                <TextField
                  label="Name"
                  value={addProviderName}
                  onChange={e => setAddProviderName(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email"
                  value={addProviderEmail}
                  onChange={e => setAddProviderEmail(e.target.value)}
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddProviderOpenFor(null); setAddProviderConflict(null); }}>Cancel</Button>
          <Button
            onClick={() => addProviderOpenFor && handleAddProvider(addProviderOpenFor)}
            variant="contained"
            disabled={savingProvider}
            startIcon={savingProvider ? <CircularProgress size={16} /> : undefined}
          >
            {savingProvider ? 'Saving...' : 'Add provider'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copilot Off explanation — why the header chip reads OFF: provider info is required,
          tenant is optional. Styled like the in-flow Copilot messages (MillOS mark + short text)
          rather than a plain text dialog, so it reads as "the AI talking to you". */}
      <Dialog open={!!copilotOffInfoOpenFor} onClose={() => setCopilotOffInfoOpenFor(null)} maxWidth="xs" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1, textAlign: 'center' }}>
            <Box sx={{ color: '#6366f1' }}>
              <MillosLogo size={65} agent />
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', lineHeight: 1.5 }}>
              In order to arrange a visit for this renewal, I need the provider's info — that's required.
              The tenant's info is optional; I can still notify the provider and coordinate a date without it.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopilotOffInfoOpenFor(null)}>Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Coordination Details Dialog — what the provider/tenant actually reported, plus the AI's
          reasoning for picking this date. Reuses the same formatAvailability() the failure-state
          inline card already uses, just surfaced on demand for the success case instead of always
          shown inline (that card has no room to spare). */}
      <Dialog open={!!coordinationDetailsOpenFor} onClose={() => setCoordinationDetailsOpenFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Availability details</DialogTitle>
        <DialogContent>
          {coordinationDetailsOpenFor && availabilityCoordination[coordinationDetailsOpenFor] && (() => {
            const coordination = availabilityCoordination[coordinationDetailsOpenFor];
            return (
              <Box sx={{ pt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Tenant</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatAvailability(coordination.tenant_availability, coordination.tenant_notes)}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Provider</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatAvailability(coordination.provider_availability, coordination.provider_notes)}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>AI reasoning</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const firstRound = (reminderEmails[coordinationDetailsOpenFor] || []).find((e: any) => e.audience === 'provider_counter')?.internal_note;
                    const final = coordination.ai_reasoning || 'No AI reasoning recorded — this date was picked manually.';
                    return firstRound ? `${firstRound} ${final}` : final;
                  })()}
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoordinationDetailsOpenFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Email Recipient Dialog */}
      <Dialog open={editEmailDialogOpen} onClose={handleCancelEditEmail} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Email Recipient</DialogTitle>
        <DialogContent>
          {editingEmailReminder && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Update who receives the email reminder for: <strong>{editingEmailReminder.certificate_title?.replace(/\.(pdf|PDF)$/, '') || 'Untitled Certificate'}</strong>
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 2 }}>Change Recipient</FormLabel>
                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Select Registered Provider</InputLabel>
                    <Select value={selectedProvider} onChange={(e) => { setSelectedProvider(e.target.value as string); setCustomContactName(''); setCustomContactEmail(''); }} label="Select Registered Provider">
                      <MenuItem value=""><em>None - Use Custom Contact</em></MenuItem>
                      {providers.map(p => <MenuItem key={p.id} value={p.id}>{p.provider_name} ({p.provider_email})</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                {!selectedProvider && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Or enter custom contact:</Typography>
                    <TextField fullWidth label="Contact Name" value={customContactName} onChange={e => setCustomContactName(e.target.value)} sx={{ mb: 2 }} required />
                    <TextField fullWidth label="Contact Email" type="email" value={customContactEmail} onChange={e => setCustomContactEmail(e.target.value)} required />
                  </Box>
                )}
              </FormControl>
              <Alert severity="info" sx={{ mt: 3 }}>The email will be sent to the updated recipient on the reminder date.</Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEditEmail}>Cancel</Button>
          <Button onClick={handleSaveEditEmail} variant="contained" disabled={isTogglingEmail || (!selectedProvider && (!customContactName || !customContactEmail))} startIcon={isTogglingEmail ? <CircularProgress size={16} /> : <EditIcon />}>
            {isTogglingEmail ? 'Updating...' : 'Update Recipient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Settings Dialog */}
      <Dialog open={emailDialogOpen} onClose={handleCancelEmailSettings} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Email Notification</DialogTitle>
        <DialogContent>
          {emailReminder && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select who should receive the email reminder for: <strong>{emailReminder.certificate_title?.replace(/\.(pdf|PDF)$/, '') || 'Untitled Certificate'}</strong>
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 2 }}>Choose Recipient</FormLabel>
                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Select Registered Provider</InputLabel>
                    <Select value={selectedProvider} onChange={e => { setSelectedProvider(e.target.value as string); setCustomContactName(''); setCustomContactEmail(''); }} label="Select Registered Provider">
                      <MenuItem value=""><em>None - Use Custom Contact</em></MenuItem>
                      {providers.map(p => <MenuItem key={p.id} value={p.id}>{p.provider_name} ({p.provider_email})</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                {!selectedProvider && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Or enter custom contact:</Typography>
                    <TextField fullWidth label="Contact Name" value={customContactName} onChange={e => setCustomContactName(e.target.value)} sx={{ mb: 2 }} required />
                    <TextField fullWidth label="Contact Email" type="email" value={customContactEmail} onChange={e => setCustomContactEmail(e.target.value)} required />
                  </Box>
                )}
              </FormControl>
              <Alert severity="info" sx={{ mt: 3 }}>An automated email will be sent on the reminder date to the selected recipient.</Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEmailSettings}>Cancel</Button>
          <Button onClick={handleSaveEmailSettings} variant="contained" disabled={isTogglingEmail || (!selectedProvider && (!customContactName || !customContactEmail))} startIcon={isTogglingEmail ? <CircularProgress size={16} /> : <Email />}>
            {isTogglingEmail ? 'Enabling...' : 'Enable Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reminder Email Draft Dialog */}
      {selectedReminderId && selectedReminderForEmail && (
        <ReminderEmailDialog
          open={emailDraftDialogOpen}
          reminderId={selectedReminderId}
          reminder={selectedReminderForEmail}
          initialEmailId={viewingEmailId || undefined}
          onClose={handleCloseEmailDraft}
          onSave={handleCloseEmailDraft}
        />
      )}
    </Box>
  );
};

export default ActiveReminders;
